import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Crop } from 'react-image-crop';
import { pxToRem } from '@withjoy/joykit/theme';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { MediaPhoto, PhotoDisplayConfig, PhotoPickerDialogProps, PICKER_TYPES, TabType } from './PhotoPickerDialog.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { ScrollWrapper } from './PhotoPickerDialog.styles';
import { DialogFooter, PhotoAlignmentSelector, PhotoCropper, PhotoPickerLibrary, TabNavigation } from './components';
import { useWebsiteOverviewTelemetry } from '@apps/admin/routes/WebsiteOverview/WebsiteOverview.telemetry';
import { useEventInfo } from '@shared/utils/eventInfo';
import { SpacingStack } from '@withjoy/joykit';
import { DialogHeader } from './components/DialogHeader/DialogHeader';
import { WebsitePhotoAlignment } from '@apps/guest/websiteHeader';
import { useTranslation } from '@shared/core';

export const PhotoPickerDialog: FC<PhotoPickerDialogProps> = props => {
  const {
    isOpen,
    handleClose,
    handleOpenFilePicker,
    isFetching,
    media,
    handleAddMedia,
    isAddingMedia,
    collectionId,
    showCount = false,
    pickerType = PICKER_TYPES.ADD_TO_COLLECTION,
    maxCount,
    minCount,
    photoSelectionHelperText,
    selectedItems = [],
    disabledItems = [],
    uploadInProgressCount = 0,
    supportsPhotoCrop = false,
    photoCropData,
    defaultTab = TabType.SELECT_PHOTO,
    enableMobileCrop = false,
  } = props;

  const [selectedItemsId, setSelectedItemsId] = useState<string[]>([]);
  const [selectedTab, setSelectedTab] = useState<TabType>(defaultTab);
  const [selectedPhotoIdToCrop, setSelectedPhotoIdToCrop] = useState<string | null>(null);
  const [photoDisplayConfig, setPhotoDisplayConfig] = useState<PhotoDisplayConfig>({
    crop: undefined,
    mobileCrop: undefined,
    focusPoint: undefined,
  });

  const { tq } = useTranslation('photoPickerDialog');

  useEffect(() => {
    if (defaultTab) {
      setSelectedTab(defaultTab);
    }
  }, [defaultTab]);

  const handleCrop = useEventCallback((crop: Crop) => {
    setPhotoDisplayConfig(prev => ({ ...prev, crop }));
  });

  const handleMobileCrop = useEventCallback((crop: Crop) => {
    setPhotoDisplayConfig(prev => ({ ...prev, mobileCrop: crop }));
  });

  const handleAlignmentChange = useEventCallback((focusPoint: WebsitePhotoAlignment) => {
    setPhotoDisplayConfig(prev => ({ ...prev, focusPoint }));
  });

  useEffect(() => {
    setSelectedItemsId([...selectedItems]);
    setSelectedPhotoIdToCrop(selectedItems[0] || null);
  }, [selectedItems, isOpen]);

  const resetPhotoDisplayConfig = useCallback(() => {
    setPhotoDisplayConfig({
      crop: undefined,
      focusPoint: undefined,
      mobileCrop: undefined,
    });
  }, []);

  useEffect(() => {
    // if selected photo is the same as the photo in photoCropData, set the crop and focus point data
    if (selectedPhotoIdToCrop === photoCropData?.photoId) {
      setPhotoDisplayConfig({
        crop: photoCropData?.crop,
        focusPoint: photoCropData?.alignment,
        mobileCrop: photoCropData?.mobileCrop,
      });
    } else {
      resetPhotoDisplayConfig();
    }
  }, [selectedPhotoIdToCrop, photoCropData, resetPhotoDisplayConfig]);

  const telemetry = useWebsiteOverviewTelemetry();

  const { eventInfo } = useEventInfo();

  const clearSelectedItemsAndClose = useEventCallback(() => {
    setSelectedItemsId([]);
    setSelectedTab(TabType.SELECT_PHOTO);
    setSelectedPhotoIdToCrop(null);
    resetPhotoDisplayConfig();
  });

  const onCloseDialog = useEventCallback(() => {
    handleClose();
    clearSelectedItemsAndClose();
  });

  const onOpenFilePicker = useEventCallback(() => {
    if (pickerType === PICKER_TYPES.ADD_TO_COLLECTION) {
      handleClose();
    }
    handleOpenFilePicker();
  });

  const updateSelectedItemsId = useEventCallback((newSelectedItemId: string) => {
    setSelectedItemsId(prevSelectedItemsId => {
      if (prevSelectedItemsId.includes(newSelectedItemId)) {
        setSelectedPhotoIdToCrop(null);
        return prevSelectedItemsId.filter(id => id !== newSelectedItemId);
      }
      if (maxCount && prevSelectedItemsId.length >= maxCount) {
        setSelectedPhotoIdToCrop(newSelectedItemId);
        // If max count is reached, remove last selected item and add new item at the end
        return [...prevSelectedItemsId.slice(0, maxCount - 1), newSelectedItemId];
      }
      setSelectedPhotoIdToCrop(newSelectedItemId);
      return [...prevSelectedItemsId, newSelectedItemId];
    });
  });

  const handleSelectAll = useEventCallback(() => {
    telemetry.selectAllPhotosCTAClicked(eventInfo?.eventId || '', collectionId || 'allPhotos');
    const allPhotoIds = media?.map(m => (m.__typename === 'MediaPhoto' ? m.id : '')).filter(id => !!id) || [];
    setSelectedItemsId(allPhotoIds);
  });

  const handleDeselectAll = useEventCallback(() => {
    telemetry.deselectAllPhotosCTAClicked(eventInfo?.eventId || '', collectionId || 'allPhotos');
    setSelectedItemsId([]);
  });

  const handleAdd = useEventCallback(() => {
    if (supportsPhotoCrop) {
      // need to navigate to crop tab if user is on select photo tab
      if (selectedTab === TabType.SELECT_PHOTO) {
        setSelectedTab(TabType.CROP);
        return;
      }
      // need to navigate to mobile crop tab if user is on crop tab and mobile crop is enabled
      if (selectedTab === TabType.CROP && enableMobileCrop) {
        setSelectedTab(TabType.MOBILE_CROP);
        return;
      }
    }
    telemetry.addMediaFromMediaLibrary(eventInfo?.eventId || '', collectionId || 'allPhotos', selectedItemsId);
    handleAddMedia({
      selectedItemsId,
      cropData: photoDisplayConfig.crop ?? null,
      alignmentData: photoDisplayConfig.focusPoint ?? null,
      mobileCropData: photoDisplayConfig.mobileCrop ?? null,
      cb: clearSelectedItemsAndClose,
    });
  });

  const selectedPhotoToCrop = useMemo<MediaPhoto | undefined>(() => {
    const selectedData = media?.find(m => m.__typename === 'MediaPhoto' && m.id === selectedPhotoIdToCrop) as MediaPhoto | undefined;
    if (selectedData) {
      return selectedData;
    } else if (photoCropData && selectedPhotoIdToCrop === photoCropData.photoId) {
      // if photo is not found in media array, we need to use the photo from photoCropData as the image will be a default template image provided by joy and not included in "All Photos"
      return {
        id: photoCropData.photoId,
        photo: {
          id: photoCropData.photoId,
          url: photoCropData.url,
          height: photoCropData.height,
          width: photoCropData.width,
        },
      };
    }
    return undefined;
  }, [media, photoCropData, selectedPhotoIdToCrop]);

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={onCloseDialog}
      hideDivider={true}
      removeBodyPadding
      hasCloseButton={false}
      disableFocusLock={true}
      drawerOverrides={{
        ...drawerOverrides,
        Content: {
          props: {
            ...drawerOverrides.Content?.props,
            boxShadow: '0px -7px 16px -8px rgba(0, 0, 0, 0.3), 0px -10px 27px -5px rgba(44, 41, 37, 0.25)',
          },
        },
        Body: { props: { ...drawerOverrides.Body?.props, paddingX: 0, paddingY: 5, overflow: 'auto', maxHeight: '100vh' } },
      }}
      dialogOverrides={{
        Content: { props: { width: '100%', maxWidth: pxToRem(788) } },
        Body: { props: { paddingX: 0, paddingY: 5, overflow: 'auto', maxHeight: 'calc(100vh - 160px)' } },
        Footer: { props: { paddingY: 5 } },
      }}
      renderFooter={() => (
        <DialogFooter
          handleDeselect={handleDeselectAll}
          handleSelectAll={handleSelectAll}
          handleCancel={onCloseDialog}
          handleAdd={handleAdd}
          isAdding={isAddingMedia}
          disableAddCTA={selectedItemsId.length === 0 || (!!minCount && selectedItemsId.length < minCount)}
          disableSelectAllCTA={selectedItemsId.length === media?.length}
          disableDeselectAllCTA={selectedItemsId.length === 0}
          pickerType={pickerType}
          selectedItemsCount={selectedItemsId.length}
          supportsPhotoCrop={supportsPhotoCrop}
          selectedTab={selectedTab}
          enableMobileCrop={enableMobileCrop}
        />
      )}
    >
      <ScrollWrapper desktopHeight={selectedTab === TabType.SELECT_PHOTO ? '100dvh' : '100%'}>
        <DialogHeader
          handleClose={onCloseDialog}
          pickerType={pickerType}
        />

        <SpacingStack
          spacing={7}
          width="100%"
          marginTop={5}
        >
          {supportsPhotoCrop && (
            <TabNavigation
              selectedTab={selectedTab}
              onSelectTab={setSelectedTab}
              isCropTabDisabled={!selectedPhotoToCrop}
              enableMobileCrop={enableMobileCrop}
              isMobileCropTabDisabled={!selectedPhotoToCrop}
            />
          )}

          {/* Renders photo picker library screen */}
          {selectedTab === TabType.SELECT_PHOTO && (
            <PhotoPickerLibrary
              isFetching={isFetching}
              media={media}
              selectedItemsId={selectedItemsId}
              disabledItems={disabledItems}
              updateSelectedItemsId={updateSelectedItemsId}
              handleOpenFilePicker={onOpenFilePicker}
              showCount={showCount}
              photoSelectionHelperText={photoSelectionHelperText}
              uploadInProgressCount={uploadInProgressCount}
            />
          )}
          {/* Show cropper for numeric aspect ratios or freeform (null) */}
          {selectedTab === TabType.CROP && selectedPhotoToCrop && photoCropData?.aspectRatio !== 'locked' && (
            <PhotoCropper
              selectedPhoto={selectedPhotoToCrop}
              // photoCropData?.aspectRatio = null meansfreeform cropping, so we need to pass undefined to enable freeform cropping
              aspectRatio={photoCropData?.aspectRatio ?? undefined}
              crop={photoDisplayConfig.crop}
              handleCrop={handleCrop}
              label={
                enableMobileCrop
                  ? tq`Pick the part of the image that you’d like to show on your site for all desktop devices.`
                  : tq`Pick the part of the image that you'd like to show on your site.`
              }
            />
          )}

          {/* Renders photo alignment screen when aspect ratio is locked*/}
          {selectedTab === TabType.CROP && selectedPhotoToCrop && photoCropData?.aspectRatio === 'locked' && (
            <PhotoAlignmentSelector
              selectedPhoto={selectedPhotoToCrop}
              alignment={photoDisplayConfig.focusPoint}
              handleFocusPointSelect={handleAlignmentChange}
            />
          )}

          {/* Renders mobile crop screen */}
          {selectedTab === TabType.MOBILE_CROP && selectedPhotoToCrop && (
            <PhotoCropper
              selectedPhoto={selectedPhotoToCrop}
              aspectRatio={photoCropData?.mobileAspectRatio ?? undefined}
              crop={photoDisplayConfig.mobileCrop}
              handleCrop={handleMobileCrop}
              label={tq`Pick the part of the image that you’d like to show on your site for all mobile devices.`}
            />
          )}
        </SpacingStack>
      </ScrollWrapper>
    </ResponsiveDialog>
  );
};

PhotoPickerDialog.displayName = 'PhotoPickerDialog';
